import React, { useEffect, useState } from 'react';
import { Dictionary } from 'lodash';
import { useTheme, useMediaQuery, makeStyles } from '@material-ui/core';
import * as d3 from 'd3';

import { TimeRange, ChartSeriesV2 } from '../../ui/chart/types';

import { TimeSeries } from '../../../models';

import BarChart from './BarChart';
import { MainChart } from './MainChart';

import { BarColor } from './Bars';
import { TagsDrawerStatus } from './AnalysisPage';
import { useDataGroups } from './useDataGroups';
import { RangeGroups } from './useRanges';
import { Chart } from 'src/state/contexts/analysisPage';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    gap: '16px',
    flexDirection: 'column',
  },
}));

export type ChartsCount = {
  bitfield: number;
  enum: number;
  chart: number;
  total: number;
};
interface Props {
  height?: number;
  xRange: {
    start: Date;
    end: Date;
  };
  tagIds: string[];
  snapshots: Dictionary<TimeSeries>;
  viewId: string;
  chart: Chart;
  fullRangeSnapshots: Dictionary<TimeSeries>;
  isDrawerOpen: TagsDrawerStatus;
  setIsDrawerOpen: (tagsDrawerStatus: TagsDrawerStatus) => void;
  fullRanges: RangeGroups;
  zoomRange: TimeRange | undefined;
  setZoomRange: (zoomRange: TimeRange | undefined) => void;
  removeTagFromChartInView: (viewId: string, chartId: string, tagId: string) => void;
  removeChartInView: (viewId: string, chartId: string) => void;
  triggeredOnly: boolean;
}

const yLabelFormatter = d3.format('.6g');
const yAxisFormatter = d3.format('.3g');
const xAxisFormatter = d3.timeFormat('%b %d, %Y %H:%M:%S');
const getTotalItems = (group: Record<string, ChartSeriesV2[]>) =>
  Object.values(group || {}).reduce((sum, items) => sum + items.length, 0);

const margins = {
  top: 16,
  bottom: 26,
  left: 0,
  right: 0,
};

export const AnalysisChart: React.FC<Props> = ({
  snapshots,
  xRange,
  tagIds,
  fullRangeSnapshots,
  fullRanges,
  zoomRange,
  setZoomRange,
  triggeredOnly,
  removeChartInView,
  height,
  viewId,
  chart,
  setIsDrawerOpen,
  removeTagFromChartInView,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const isSmallPortrait = useMediaQuery(
    `${theme.breakpoints.down('xs')} and (orientation: portrait)`
  );
  const isSmallLandscape = useMediaQuery(
    `${theme.breakpoints.down('sm')} and (orientation: landscape)`
  );
  const isSmallScreen = isSmallPortrait || isSmallLandscape;

  const [chartsCount, setChartsCount] = useState<ChartsCount>({
    bitfield: 0,
    enum: 0,
    chart: 0,
    total: 0,
  });

  const lineGroups = useDataGroups(tagIds, snapshots);

  const editPointsHandler = () => {
    setIsDrawerOpen({ isOpen: true, viewId: viewId, chartId: chart.id });
  };

  const removeTagFromChartInViewHandler = (tagId: string) => {
    removeTagFromChartInView(viewId, chart.id, tagId);
  };

  const removeChartInViewHandler = () => {
    removeChartInView(viewId, chart.id);
  };

  useEffect(() => {
    const bitfieldCount = getTotalItems(lineGroups.bitfield);
    const enumCount = getTotalItems(lineGroups.enum);
    const chartCount = getTotalItems(lineGroups.chart) > 0 ? 1 : 0;

    const totals = {
      bitfield: bitfieldCount,
      enum: enumCount,
      chart: chartCount,
      total: bitfieldCount + enumCount + chartCount,
    };
    setChartsCount(totals);
  }, [lineGroups.bitfield, lineGroups.enum, lineGroups.chart]);

  return (
    <div className={classes.container}>
      <MainChart
        chartLabel={chart.label}
        chartsCount={chartsCount}
        snapshots={snapshots}
        height={height}
        tagIds={tagIds}
        xRange={xRange}
        margins={margins}
        lineGroups={lineGroups.chart}
        zoomRange={zoomRange}
        setZoomRange={setZoomRange}
        fullRanges={fullRanges.chart}
        isSmallScreen={isSmallScreen}
        xAxisFormatter={xAxisFormatter}
        yAxisFormatter={yAxisFormatter}
        yLabelFormatter={yLabelFormatter}
        removeTagFromChartGroup={removeTagFromChartInViewHandler}
        editPointsHandler={editPointsHandler}
        removeChartInView={removeChartInViewHandler}
      />

      <BarChart
        chartLabel={chart.label}
        chartsCount={chartsCount}
        lineGroups={lineGroups.bitfield}
        snapshots={snapshots}
        fullRangeSnapshots={fullRangeSnapshots}
        tagIds={tagIds}
        xRange={xRange}
        margins={margins}
        fullRanges={fullRanges.bitfield}
        zoomRange={zoomRange}
        onZoomChange={setZoomRange}
        color={BarColor.RED}
        isBitfield
        removeTagFromChartGroup={removeTagFromChartInViewHandler}
        editPointsHandler={editPointsHandler}
        triggeredOnly={triggeredOnly}
        removeChartInView={removeChartInViewHandler}
      />

      <BarChart
        chartLabel={chart.label}
        chartsCount={chartsCount}
        lineGroups={lineGroups.enum}
        snapshots={snapshots}
        fullRangeSnapshots={fullRangeSnapshots}
        tagIds={tagIds}
        xRange={xRange}
        margins={margins}
        fullRanges={fullRanges.enum}
        zoomRange={zoomRange}
        onZoomChange={setZoomRange}
        color={BarColor.GREEN}
        removeTagFromChartGroup={removeTagFromChartInViewHandler}
        editPointsHandler={editPointsHandler}
        triggeredOnly={triggeredOnly}
        removeChartInView={removeChartInViewHandler}
      />
    </div>
  );
};
